import React from 'react';
import { useAuth0, RedirectLoginOptions } from '@auth0/auth0-react';
import { User as Auth0User } from '@auth0/auth0-spa-js';
import Base64 from 'base-64';
import { AuthenticationClient } from './client';
import { LoginOptions, User } from './types';
import { getUsersClientName } from './service';
import { useAnalytics } from '../analytics';
import { useFeedback } from '../feedback';
import { getUserId, isInternal, isTestUser, shouldDisableAnalytics } from './utils';
import { authorisation } from '../authorisation';
import { resetAuthorisationCache } from '../authorisation/authorisation';

export const ACCEPT_USE_POLICY = '/callback/accept-use-policy';
export const PROFILE_ENRICH = '/callback/profile-enrichment';

const createUserObject = async (parsedToken: any, user: Auth0User, accessToken: string): Promise<User> => {
  const country = parsedToken['https://gfk.com']?.country;
  const clientId = parsedToken['https://gfk.com']?.client;
  const userGroups = parsedToken['https://gfk.com']?.user_groups;
  const magicLinkId = parsedToken['https://gfk.com']?.magic_link_id;
  const groups = parsedToken['https://gfk.com']?.groups;
  const userId = parsedToken.userId;
  // for now we are using the userId prefix as a fallback to determine if a user is internal
  // TODO: remove user id check after isInternalUser claim is set for all internal users
  const isInternalUser =
    typeof parsedToken['isInternalUser'] === 'boolean' ? parsedToken['isInternalUser'] : isInternal({ userId });
  const clientName = await getUsersClientName(accessToken);
  // Retrieve the user's email from Auth0 metadata (userMetaEmail).
  // If not available, fallback to the email provided in the Auth0 user object.
  const userEmail = user.user_metadata?.userMetaEmail || user.email;

  return {
    email: userEmail,
    name: user.name,
    firstName: user.given_name,
    lastName: user.family_name,
    sub: userId,
    userId,
    clientId: clientId ? parseInt(clientId) : undefined,
    isInternalUser,
    clientName,
    country,
    userGroupIds: userGroups,
    magicLinkId,
    groups,
  };
};

const setWalkMeUser = (user: User, subscriptions: string, disable: boolean) => {
  window['walkMeUser'] = {
    id: user?.userId,
    country: user?.country,
    subscriptions,
    disable,
  };
};

function Auth0Adapter() {
  const { loginWithRedirect, logout, isAuthenticated, isLoading, user, getAccessTokenSilently, error } = useAuth0();
  const { track, identify, group } = useAnalytics();
  const { updateUserData } = useFeedback();
  const isAuth0RedirectCallback = window.location.href.match(`${ACCEPT_USE_POLICY}|${PROFILE_ENRICH}`);

  React.useEffect(() => {
    AuthenticationClient.getInstance().setFns(loginAdapter, logoutAdapter, getAccessTokenAdapter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const updateClient = async () => {
      const authClient = AuthenticationClient.getInstance();

      if (!isAuthenticated || !user) {
        authClient.update(isAuthenticated, isLoading, null, error);
        return;
      }

      const accessToken = await getAccessTokenSilently();
      if (!accessToken) {
        authClient.update(isAuthenticated, isLoading, null, error);
        return;
      }

      const parsedToken = JSON.parse(Base64.decode(accessToken.split('.')[1]));
      const cpUser = await createUserObject(parsedToken, user, accessToken);

      const wasAuthenticated = authClient.isAuthenticated();
      const knownUser = authClient.getUser();

      const sameUser = wasAuthenticated === isAuthenticated && cpUser.userId === knownUser?.userId;
      if (!sameUser) {
        track('Login');
      }

      authClient.update(isAuthenticated, isLoading, cpUser, error);

      if (cpUser) {
        await handleAuthenticatedUser(cpUser);
      }
    };

    const handleAuthenticatedUser = async (cpUser: User) => {
      resetAuthorisationCache();

      const products = await authorisation.getAuthorisedProductNames();
      const subscriptions = products.join(',');

      identify(undefined, { subscriptions });

      const userId = getUserId(cpUser.userId);
      const disableIntegrations =
        !userId || isInternal(cpUser) || isTestUser(cpUser.country) || shouldDisableAnalytics();

      setWalkMeUser(cpUser, subscriptions, disableIntegrations);

      updateUserData(isAuthenticated, {
        id: cpUser.userId,
        firstName: cpUser.firstName,
        lastName: cpUser.lastName,
        email: cpUser.email,
        clientId: cpUser.clientId,
      });

      if (cpUser.clientId) {
        group();
      }
    };

    updateClient();
  }, [isAuthenticated, isLoading, user, error, getAccessTokenSilently, identify, track, group, updateUserData]);

  const loginAdapter = (loginOptions?: LoginOptions): void => {
    const options: RedirectLoginOptions = {
      redirectUri: loginOptions?.redirectUri || `${window.location.origin}/callback`,
      prompt: loginOptions?.promptLogin ? 'login' : undefined,
    };
    loginWithRedirect(options);
  };

  const logoutAdapter = (): void => {
    setWalkMeUser(undefined, undefined, false);

    global['analytics']?.reset();
    resetAuthorisationCache();
    logout({ returnTo: `${window.location.origin}/logout` });
  };

  const getAccessTokenAdapter = (ignoreCache: boolean = false): Promise<string> => {
    if (isAuth0RedirectCallback) {
      return Promise.resolve('');
    }
    return getAccessTokenSilently({ ignoreCache });
  };

  return null;
}

export { Auth0Adapter };
